import type { LessonConfig } from '../../engine/plant/types';

/**
 * Training lessons 1–5. Each is a scripted walk through the steady-shift scenario with
 * checkpoints the engine can verify, so a lesson about bumpless transfer cannot be
 * skipped past without actually doing the transfer.
 */
export const lessons: LessonConfig[] = [
  {
    id: 'L1',
    number: 1,
    title: 'Manual and automatic — what a loop is',
    summary: 'Three numbers, two modes, and the one idea the rest of the game is built on.',
    scenarioId: 'steady',
    steps: [
      {
        id: 'intro',
        title: 'Three numbers',
        focusLoop: 'PIC-701',
        body:
          'Open the PIC-701 faceplate from the P&ID — the bubble near the distribution header.\n\n' +
          'Every control loop is three numbers.\n\n' +
          '**PV**, the process variable: what the plant is actually doing. Here, the pressure in ' +
          'the distribution main, measured by a transmitter.\n\n' +
          '**SP**, the setpoint: what you want it to be.\n\n' +
          '**OP**, the output: what the controller is doing about it. Here, the speed demand to ' +
          'the high-lift pumps, in per cent.\n\n' +
          'The controller has exactly one job: move OP until PV equals SP.',
      },
      {
        id: 'to-man',
        title: 'Take it to manual',
        focusLoop: 'PIC-701',
        check: { op: 'mode', loop: 'PIC-701', is: 'MAN' },
        body:
          'Press **MAN** on the faceplate.\n\n' +
          'In manual, the controller stops calculating and you own the output directly. Notice ' +
          'two things: the output did not move when you pressed the button — that is *bumpless ' +
          'transfer*, and it is not optional on a real plant — and the setpoint has jumped to sit ' +
          'on top of the PV. That is *setpoint tracking*: it means that when you go back to ' +
          'automatic, there is no step change waiting for you.',
      },
      {
        id: 'drive',
        title: 'Drive the plant by hand',
        focusLoop: 'PIC-701',
        check: { op: '>', tag: 'PIC-701.OP', value: 90 },
        body:
          'Type **92** into the OP box and press Enter.\n\n' +
          'You have just told the high-lift pumps to run at 92 per cent. Watch the pressure ' +
          'climb. This is what an operator did all day before controllers existed, and it is what ' +
          'you fall back to when a measurement fails.',
      },
      {
        id: 'watch',
        title: 'Notice what manual costs you',
        focusLoop: 'PIC-701',
        autoAdvanceS: 25,
        body:
          'The pressure has settled somewhere above setpoint and it will stay there, because ' +
          'nothing is correcting it. If the demand changes, it will drift again, and only you ' +
          'will notice.\n\n' +
          'That is the whole argument for automatic control: not that it is cleverer than you, ' +
          'but that it never gets distracted.',
      },
      {
        id: 'to-auto',
        title: 'Back to automatic',
        focusLoop: 'PIC-701',
        check: { op: 'mode', loop: 'PIC-701', is: 'AUTO' },
        body:
          'Press **AUTO**.\n\n' +
          'Again the output did not jump. The controller has taken over from exactly where you ' +
          'left it and is now working the difference between PV and SP down to nothing.',
      },
      {
        id: 'setpoint',
        title: 'Now change what you want',
        focusLoop: 'PIC-701',
        check: { op: '>', tag: 'PIC-701.SP', value: 4.75 },
        body:
          'Set the SP to **4.8** barg.\n\n' +
          'You have stopped telling the plant *how* and started telling it *what*. The controller ' +
          'works out the how. Watch the output move, overshoot slightly, and settle.',
      },
      {
        id: 'done',
        title: 'That is a control loop',
        focusLoop: 'PIC-701',
        body:
          'Put the setpoint back to **4.5** and leave the loop in AUTO.\n\n' +
          'Everything else in this game is a variation on what you just did: more loops, ' +
          'connected to each other, on processes that answer more slowly and less obligingly. ' +
          'The three numbers never change.',
      },
    ],
  },

  {
    id: 'L2',
    number: 2,
    title: 'Proportional only — gain and offset',
    summary: 'Why a proportional controller always leaves the job half done.',
    scenarioId: 'steady',
    steps: [
      {
        id: 'setup',
        title: 'Strip the controller back',
        focusLoop: 'PIC-701',
        onEnter: [
          { do: 'setMode', loop: 'PIC-701', mode: 'AUTO' },
          { do: 'setSp', loop: 'PIC-701', value: 4.5 },
          { do: 'setTuning', loop: 'PIC-701', kc: 1.2, ti: 0, td: 0 },
        ],
        body:
          'We have set PIC-701 to proportional action only: Kc = 1.2, Ti = 0 (integral off), ' +
          'Td = 0.\n\n' +
          'A proportional controller does exactly one thing:\n\n' +
          '  **OP = Kc × error + bias**\n\n' +
          'Nothing else. The output is a fixed multiple of how wrong the plant is right now.',
      },
      {
        id: 'offset',
        title: 'Watch the offset appear',
        focusLoop: 'PIC-701',
        check: { op: 'for', term: { op: '>', tag: 'PIC-701.SP', value: 4.75 }, seconds: 45 },
        body:
          'Move the setpoint to **4.8** barg and wait a minute.\n\n' +
          'The pressure will rise, but it will *not* reach 4.8. It settles short and stays short. ' +
          'That gap is **offset**, and it is not a fault — it is arithmetic. To hold the higher ' +
          'pressure the pumps need a higher output, and the only way a proportional controller ' +
          'can produce a higher output is to keep an error. No error, no extra output.',
      },
      {
        id: 'gain',
        title: 'Turn the gain up',
        focusLoop: 'PIC-701',
        check: { op: '>', tag: 'PIC-701.KC', value: 3.5 },
        body:
          'On the Tuning tab, set **Kc to 4** and watch.\n\n' +
          'The offset shrinks — four times the gain, roughly a quarter of the offset. But the ' +
          'approach is livelier, and the output is starting to fidget against the measurement ' +
          'noise.',
      },
      {
        id: 'toohigh',
        title: 'And too far',
        focusLoop: 'PIC-701',
        check: { op: '>', tag: 'PIC-701.KC', value: 11 },
        body:
          'Now set **Kc to 12**.\n\n' +
          'The offset is nearly gone and the loop is nearly unusable: it rings, and every scrap ' +
          'of transmitter noise is being multiplied by twelve and sent to the pumps. Gain is not ' +
          'free. Somewhere between "sloppy" and "screaming" is a compromise, and proportional ' +
          'action alone forces you to choose one.',
      },
      {
        id: 'summary',
        title: 'Why this matters',
        focusLoop: 'PIC-701',
        onEnter: [{ do: 'setTuning', loop: 'PIC-701', kc: 1.2, ti: 6, td: 0 }],
        body:
          'Proportional action is fast and it is stable, and it never quite finishes the job.\n\n' +
          'Every real controller in this game has integral action for exactly this reason. The ' +
          'next lesson shows what it does.\n\n' +
          '(The tuning has been restored.)',
      },
    ],
  },

  {
    id: 'L3',
    number: 3,
    title: 'Integral action — and reset windup',
    summary: 'How the offset gets removed, and the trap that comes with it.',
    scenarioId: 'steady',
    steps: [
      {
        id: 'setup',
        title: 'Add integral action',
        focusLoop: 'PIC-701',
        onEnter: [
          { do: 'setMode', loop: 'PIC-701', mode: 'AUTO' },
          { do: 'setSp', loop: 'PIC-701', value: 4.5 },
          { do: 'setTuning', loop: 'PIC-701', kc: 1.2, ti: 6, td: 0 },
        ],
        body:
          'Ti = 6 seconds. The integral term keeps a running total of the error and adds it to ' +
          'the output:\n\n' +
          '  **OP = Kc × [ error + (1/Ti) ∫ error dt ]**\n\n' +
          'As long as any error remains, the total keeps growing and the output keeps moving. ' +
          'The loop can only stop when the error is exactly zero. That is the whole trick.',
      },
      {
        id: 'nooffset',
        title: 'No offset',
        focusLoop: 'PIC-701',
        check: { op: 'for', term: { op: '<', tag: 'PIC-701.ERR', value: 0.02 }, seconds: 30 },
        body:
          'Move the setpoint to **4.8** again.\n\n' +
          'This time it arrives. Slower than proportional alone would have got close, but it ' +
          'arrives, and it stays. Ti is the *time* the integral takes to repeat the proportional ' +
          'contribution: smaller is more aggressive.',
      },
      {
        id: 'windup-setup',
        title: 'Now the trap',
        focusLoop: 'PIC-701',
        onEnter: [
          { do: 'setTuning', loop: 'PIC-701', kc: 1.2, ti: 6 },
          { do: 'setSp', loop: 'PIC-701', value: 4.5 },
        ],
        body:
          'Integral action has one dangerous property: it does not know when it is beaten.\n\n' +
          'If the output hits a limit — a valve fully open, a pump at maximum speed — the error ' +
          'stays, so the integral keeps accumulating. When the process finally responds, the ' +
          'controller has to unwind all of that before it starts backing off. The result is a ' +
          'huge overshoot. This is **reset windup**.',
      },
      {
        id: 'windup-do',
        title: 'Ask for the impossible',
        focusLoop: 'PIC-701',
        check: { op: '>', tag: 'PIC-701.SP', value: 5.7 },
        body:
          'Set the SP to **5.9** barg. The pumps cannot make that against this demand, so the ' +
          'output will saturate at 100 per cent.\n\n' +
          'Wait a minute, then put the setpoint back to **4.5** and watch what happens.',
      },
      {
        id: 'windup-watch',
        title: 'What you just saw',
        focusLoop: 'PIC-701',
        autoAdvanceS: 40,
        body:
          'The pressure came back promptly, without a long period of stubbornly high output. ' +
          'That is not luck: every controller in this game uses **back-calculation ' +
          'anti-windup** — while the output is saturated, the integral is bled back toward what ' +
          'the output can actually achieve, so there is nothing to unwind.\n\n' +
          'Controllers without it — and there are still plenty in service — will sit at their ' +
          'limit for minutes after the process is back in range.',
      },
      {
        id: 'done',
        title: 'Restore',
        focusLoop: 'PIC-701',
        onEnter: [
          { do: 'setSp', loop: 'PIC-701', value: 4.5 },
          { do: 'setTuning', loop: 'PIC-701', kc: 1.2, ti: 6, td: 0 },
        ],
        body:
          'PIC-701 is back to its design tuning.\n\n' +
          'Proportional gets you there fast, integral gets you there exactly, and anti-windup ' +
          'stops integral from doing something stupid while it waits.',
      },
    ],
  },

  {
    id: 'L4',
    number: 4,
    title: 'Derivative — and why it is usually off',
    summary: 'The third term, what it is for, and why most loops in a real plant do not use it.',
    scenarioId: 'steady',
    steps: [
      {
        id: 'intro',
        title: 'The third term',
        focusLoop: 'AIC-602',
        body:
          'Derivative action responds to the *rate of change* of the measurement:\n\n' +
          '  **OP = Kc × [ error + (1/Ti) ∫ error dt + Td × d(−PV)/dt ]**\n\n' +
          'It is anticipation. If the PV is heading toward the setpoint quickly, derivative pulls ' +
          'the output back before it arrives, so it does not overshoot. On a slow, clean ' +
          'measurement — a big temperature — it earns its place.',
      },
      {
        id: 'noise',
        title: 'Now look at a noisy one',
        focusLoop: 'PIC-701',
        onEnter: [{ do: 'setTuning', loop: 'PIC-701', kc: 1.2, ti: 6, td: 12 }],
        check: { op: '>', tag: 'PIC-701.TD', value: 6 },
        body:
          'We have put **Td = 12 s** on the distribution pressure loop. Open its faceplate and ' +
          'look at the output.\n\n' +
          'The pressure transmitter has a small amount of noise, as every real transmitter does. ' +
          'Derivative differentiates that noise, and the output is now jittering. Every one of ' +
          'those wiggles is a real command to a real variable-speed drive.',
      },
      {
        id: 'filter',
        title: 'The filter is not optional',
        focusLoop: 'PIC-701',
        autoAdvanceS: 20,
        body:
          'The derivative in this controller is filtered with a time constant of Td/N, where N ' +
          'defaults to 10. That caps the high-frequency gain at Kc × N — here, twelve times the ' +
          'proportional gain. Without the filter it would be unbounded, and the output would be ' +
          'pure noise.\n\n' +
          'The Tuning tab lets you change N. Lower N means heavier filtering and less useful ' +
          'derivative.',
      },
      {
        id: 'kick',
        title: 'Derivative on PV, not on error',
        focusLoop: 'PIC-701',
        body:
          'Notice the button on the Tuning tab that says **D on PV**.\n\n' +
          'If derivative acted on the *error*, a setpoint step would be an instantaneous infinite ' +
          'rate of change, and the output would spike violently — "derivative kick". Acting on ' +
          'the PV alone gives the same benefit during a disturbance with none of the kick. It is ' +
          'the default here and on every competent DCS.\n\n' +
          'Press the button to switch it to error, change the setpoint, and watch the output ' +
          'jump. Then put it back.',
      },
      {
        id: 'done',
        title: 'When to use it',
        focusLoop: 'PIC-701',
        onEnter: [
          { do: 'setTuning', loop: 'PIC-701', kc: 1.2, ti: 6, td: 0 },
          { do: 'setSp', loop: 'PIC-701', value: 4.5 },
        ],
        body:
          'Rule of thumb: derivative helps on temperature and on anything with a large lag and a ' +
          'clean measurement. It hurts on flow, on pressure, and on anything with a noisy or ' +
          'stepped signal.\n\n' +
          'Look at the tuning of the loops on this plant: not one flow loop has derivative. The ' +
          'reactor temperature controller on the chemical unit does.',
      },
    ],
  },

  {
    id: 'L5',
    number: 5,
    title: 'Tuning from a step test',
    summary: 'Measure the process, then calculate the tuning. Stop guessing.',
    scenarioId: 'steady',
    steps: [
      {
        id: 'intro',
        title: 'Why not just fiddle?',
        focusLoop: 'AIC-601',
        body:
          'You can tune a loop by trial and error, and plenty of people do. It takes a long time, ' +
          'it upsets the plant, and you never know whether you have finished.\n\n' +
          'The alternative is to spend ten minutes measuring what the process actually does, and ' +
          'then calculate a tuning from it. We are going to do that on AIC-601, the chlorine ' +
          'residual controller — a slow loop with real dead time, which is exactly the kind that ' +
          'punishes guessing.',
      },
      {
        id: 'man',
        title: 'Manual first',
        focusLoop: 'AIC-601',
        check: { op: 'mode', loop: 'AIC-601', is: 'MAN' },
        body:
          'Open AIC-601 and put it in **MAN**.\n\n' +
          'A step test measures the *process*, not the loop. The controller has to be out of the ' +
          'way, or you are measuring the two of them together.',
      },
      {
        id: 'start',
        title: 'Start recording, then bump',
        focusLoop: 'AIC-601',
        body:
          'Go to the **Tuning** tab and press **Start**. Then press **Bump 5 %**.\n\n' +
          'You have just made a step change in the dosing pump output. Now wait — and this loop ' +
          'is slow, so speed the simulation up to 20× or 100× and give it twenty simulated ' +
          'minutes.',
      },
      {
        id: 'fit',
        title: 'Fit the model',
        focusLoop: 'AIC-601',
        body:
          'When the residual has clearly settled at a new value, press **Fit**.\n\n' +
          'Three numbers come back:\n\n' +
          '**Kp** — the process gain: how much the PV moves per unit of output. Big gain, small ' +
          'controller gain.\n\n' +
          '**τ** — the time constant: how long the process takes to get most of the way there.\n\n' +
          '**θ** — the dead time: how long before *anything* happens at all. Dead time is the ' +
          'enemy. It is why this loop cannot be tuned tightly no matter how clever you are.',
      },
      {
        id: 'apply',
        title: 'Choose a rule',
        focusLoop: 'AIC-601',
        body:
          'Four tunings are offered, each with its reasoning.\n\n' +
          '**SIMC** aims for a specific closed-loop speed, set by the τc slider. It is the modern ' +
          'default and it is forgiving.\n\n' +
          '**Ziegler–Nichols** targets quarter-amplitude decay — a response that overshoots about ' +
          '25 per cent and rings twice. It was designed in 1942 for chart recorders and it is ' +
          'aggressive by modern standards.\n\n' +
          '**Cohen–Coon** is for dead-time-dominant processes, which this one is.\n\n' +
          'Apply the **Ziegler–Nichols (PI)** tuning, put the loop back in AUTO, and change the ' +
          'setpoint by 0.2 mg/L.',
      },
      {
        id: 'compare',
        title: 'Now try SIMC',
        focusLoop: 'AIC-601',
        body:
          'Ring-y, wasn’t it?\n\n' +
          'Go back to the Tuning tab, apply the **SIMC (PI)** tuning instead, and make the same ' +
          'setpoint change. Same process, same measurement, very different behaviour — and the ' +
          'SIMC response gets there at about the same time with a fraction of the valve movement.\n\n' +
          'Slide τc up to 2× or 3× θ and try again for an even gentler response. There is no ' +
          'single right answer; there is a trade between speed and robustness, and now you can ' +
          'choose where to sit on it.',
      },
      {
        id: 'done',
        title: 'Restore',
        focusLoop: 'AIC-601',
        onEnter: [
          { do: 'setTuning', loop: 'AIC-601', kc: 0.25, ti: 800, td: 0 },
          { do: 'setMode', loop: 'AIC-601', mode: 'AUTO' },
          { do: 'setSp', loop: 'AIC-601', value: 1.0 },
        ],
        body:
          'AIC-601 is back to its design tuning — which, as it happens, is SIMC with τc = 2θ.\n\n' +
          'The Tuning Challenges in the main menu score you on the integrated absolute error over ' +
          'a fixed set of setpoint changes and disturbances. That is where to practise.',
      },
    ],
  },
];
